import { useState } from "react";

const pillars = [
  {
    key: "sourcing",
    label: "Product Sourcing",
    icon: "ri-search-eye-line",
    color: "text-[#C9A227]",
    activeBg: "bg-[#C9A227]",
    lightBg: "bg-[#C9A227]/10",
    borderColor: "border-[#C9A227]",
    steps: [
      {
        icon: "ri-file-list-3-line",
        title: "Submit Your Brief",
        description:
          "Tell us what you need — product type, specifications, target price, quantity, and timeline. Our intake form takes under 5 minutes.",
      },
      {
        icon: "ri-group-line",
        title: "Supplier Matching",
        description:
          "Within a few business days, our team searches our verified network and identifies the best supplier candidates that fit your requirements.",
      },
      {
        icon: "ri-file-chart-line",
        title: "Quote Comparison",
        description:
          "We gather quotes, negotiate pricing on your behalf, and present you with a clear side-by-side comparison — no back-and-forth required.",
      },
      {
        icon: "ri-box-3-line",
        title: "Sample & Review",
        description:
          "Request samples from your preferred supplier. We coordinate delivery and quality review before you make any purchase commitment.",
      },
      {
        icon: "ri-truck-line",
        title: "Order & Deliver",
        description:
          "We manage production follow-up, pre-shipment inspection, freight coordination, and final delivery straight to your facility.",
      },
    ],
  },
  {
    key: "wholesale",
    label: "Wholesale Supply",
    icon: "ri-store-3-line",
    color: "text-[#3A506B]",
    activeBg: "bg-[#3A506B]",
    lightBg: "bg-[#D9E2EC]",
    borderColor: "border-[#3A506B]",
    steps: [
      {
        icon: "ri-shopping-bag-3-line",
        title: "Browse or Request",
        description:
          "Explore our wholesale catalog across 40+ categories, or submit a custom bulk request with your specific product specs and volume needs.",
      },
      {
        icon: "ri-links-line",
        title: "Direct Supplier Access",
        description:
          "We connect you straight to the manufacturer or distributor — no middleman markup, no reseller markups eating into your margin.",
      },
      {
        icon: "ri-shake-hands-line",
        title: "Negotiate Terms",
        description:
          "Lock in bulk pricing, minimum order quantities, and payment terms directly — with our procurement team facilitating every conversation.",
      },
      {
        icon: "ri-shield-check-line",
        title: "Pre-Shipment QC",
        description:
          "Before goods leave the facility, our quality control team inspects to ensure everything precisely matches your confirmed specifications.",
      },
      {
        icon: "ri-ship-2-line",
        title: "Freight & Delivery",
        description:
          "We coordinate sea, air, or land freight, handle all customs documentation, and deliver directly to your warehouse — on time.",
      },
    ],
  },
  {
    key: "inventory",
    label: "Inventory Solutions",
    icon: "ri-archive-drawer-line",
    color: "text-[#C9A227]",
    activeBg: "bg-[#C9A227]",
    lightBg: "bg-[#C9A227]/10",
    borderColor: "border-[#C9A227]",
    steps: [
      {
        icon: "ri-archive-line",
        title: "List Your Inventory",
        description:
          "Submit your overstock details: product type, condition, quantity, and asking price. Our listing process takes just a few minutes to complete.",
      },
      {
        icon: "ri-bar-chart-2-line",
        title: "Market Assessment",
        description:
          "Our team evaluates your inventory value and matches it against real-time buyer demand across our active buyer network.",
      },
      {
        icon: "ri-user-search-line",
        title: "Buyer Matching",
        description:
          "We connect you with pre-qualified bulk buyers who are actively looking for your exact type of merchandise — no cold outreach needed.",
      },
      {
        icon: "ri-money-dollar-circle-line",
        title: "Negotiate & Close",
        description:
          "Our team facilitates the negotiation to secure the best achievable price for your inventory — and moves it fast.",
      },
      {
        icon: "ri-secure-payment-line",
        title: "Logistics & Payment",
        description:
          "We coordinate buyer pickup or outbound delivery, then ensure payment is confirmed and released once the deal is closed.",
      },
    ],
  },
];

export default function HowItWorks() {
  const [activeKey, setActiveKey] = useState("sourcing");
  const active = pillars.find((p) => p.key === activeKey)!;

  return (
    <section id="how-it-works" className="py-24 bg-[#222831]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col items-center text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A227] mb-3">
            How It Works
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            A Clear Process for Every Pillar
          </h2>
          <p className="mt-4 text-base text-white/60 max-w-xl leading-relaxed">
            No guesswork, no black boxes. Each of our three service pillars follows
            a defined, transparent five-step flow — so you always know what happens next.
          </p>
        </div>

        {/* Pillar Tabs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          {pillars.map((p) => (
            <button
              key={p.key}
              onClick={() => setActiveKey(p.key)}
              className={`flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap border-2 ${
                activeKey === p.key
                  ? `${p.activeBg} text-white border-transparent`
                  : "bg-white/5 text-white/60 border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >
              <i className={`${p.icon} text-base`}></i>
              {p.label}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line — desktop only */}
          <div className="hidden lg:block absolute top-11 left-[10%] right-[10%] h-px bg-white/10 z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 relative z-10">
            {active.steps.map((step, idx) => (
              <div key={step.title} className="flex flex-col items-center text-center gap-4">
                {/* Step number + icon circle */}
                <div className="relative">
                  <div className={`w-[88px] h-[88px] flex items-center justify-center rounded-full ${active.lightBg} border-2 ${active.borderColor}`}>
                    <i className={`${step.icon} text-3xl ${active.color}`}></i>
                  </div>
                  <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full ${active.activeBg} flex items-center justify-center`}>
                    <span className="text-white text-xs font-extrabold leading-none">{idx + 1}</span>
                  </div>
                </div>

                {/* Text */}
                <div>
                  <h3 className="text-sm font-bold text-white leading-snug mb-1.5">
                    {step.title}
                  </h3>
                  <p className="text-xs text-white/55 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Mobile connector arrow */}
                {idx < active.steps.length - 1 && (
                  <div className="lg:hidden w-px h-6 bg-white/15 mx-auto" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 rounded-2xl bg-white/5 border border-white/10 px-8 py-7 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-full ${active.lightBg} flex-shrink-0`}>
              <i className={`${active.icon} text-xl ${active.color}`}></i>
            </div>
            <div>
              <p className="text-white font-bold text-base">
                Ready to start with {active.label}?
              </p>
              <p className="text-white/50 text-sm mt-0.5">
                Get started in minutes — our team will guide you through every step.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="px-7 py-3 rounded-full bg-[#C9A227] text-white text-sm font-bold hover:bg-[#b08f1e] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            Get Started
            <i className="ri-arrow-right-line text-base"></i>
          </button>
        </div>

      </div>
    </section>
  );
}
